import "./index.css";

import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import { RadioGroup } from "@tql/ui-components-react/kendo/inputs";
import { Hint } from "@tql/ui-components-react/kendo/labels";
import { Upload } from "@tql/ui-components-react/kendo/upload";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { MessageBox } from "@tql/ui-components-react/tql/layout/beta";
import {
	download_01Line,
	upload_01Line,
} from "@tql/ui-components-react/untitled/icons";
import type { HTMLAttributes, ReactElement } from "react";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TraxInfo } from "~/shared/components/trax-info/trax-info.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { getBaseUrl } from "~/shared/utils/get-base-url.ts";

import {
	CARRIER_PREFERENCE_OPTIONS,
	CARRIER_PREFERENCE_TOOLTIPS,
} from "./constants/bulk-upload-constants.ts";
import { useBulkUpload } from "./hooks/use-bulk-upload.ts";
import type {
	BulkUploadModalProps,
	BulkUploadTemplateType,
	CarrierPreference,
} from "./types/bulk-upload-types.ts";
import { downloadTemplate } from "./utils/bulk-upload-utils.ts";

const handleDownloadTemplate = (templateType: BulkUploadTemplateType) => {
	downloadTemplate(getBaseUrl(), templateType);
};

const SelectFilesMessage = () => <>Choose file</>;

const isFeatureAvailable = (isReady: boolean, isEnabled: boolean): boolean =>
	isReady && isEnabled;

const shouldShowCarrierPreference = (
	isBulkTenderAvailable: boolean,
	fileCount: number,
	templateType: BulkUploadTemplateType | null,
): boolean =>
	isBulkTenderAvailable && fileCount > 0 && templateType === "tender";

const isUploadDisabled = ({
	isUploading,
	fileCount,
	templateType,
	carrierPreference,
	isLookupDataReady,
	isBulkTenderAvailable,
}: {
	isUploading: boolean;
	fileCount: number;
	templateType: BulkUploadTemplateType | null;
	carrierPreference: CarrierPreference | undefined;
	isLookupDataReady: boolean;
	isBulkTenderAvailable: boolean;
}): boolean =>
	isUploading ||
	fileCount === 0 ||
	!templateType ||
	(templateType === "tender" && !isBulkTenderAvailable) ||
	(templateType === "tender" && !carrierPreference) ||
	!isLookupDataReady;

// Kendo types the radio label as a string, so the tooltip is appended to the list item instead.
const CarrierPreferenceItem = (props: HTMLAttributes<HTMLLIElement>) => {
	const { children, ...listItemProps } = props;
	const radio = children as ReactElement<{ value?: string }> | undefined;
	const tooltip = CARRIER_PREFERENCE_TOOLTIPS[radio?.props?.value ?? ""];

	return (
		<li {...listItemProps}>
			{children}
			{tooltip ? <TraxInfo tooltip={tooltip} /> : null}
		</li>
	);
};

export const BulkUploadModal = ({
	toggleBulkUploadModal,
}: BulkUploadModalProps) => {
	const {
		files,
		errors,
		isUploading,
		isInternal,
		templateType,
		carrierPreference,
		isLookupDataReady,
		handleAdd,
		handleRemove,
		handleCarrierPreferenceChange,
		uploadFile,
		submissionResult,
		confirmSubmissionSuccess,
		dismissSubmissionResult,
	} = useBulkUpload(toggleBulkUploadModal);
	const {
		isReady: isExternalBulkQuoteReady,
		isEnabled: isExternalBulkQuoteEnabled,
	} = useFeatureFlagGate("traxLtlExternalBulkQuote");
	const { isReady: isBulkTenderReady, isEnabled: isBulkTenderEnabled } =
		useFeatureFlagGate("traxLtlBulkTender");
	const canDownloadQuoteTemplate =
		isInternal || (isExternalBulkQuoteReady && isExternalBulkQuoteEnabled);
	const isBulkTenderAvailable = isFeatureAvailable(
		isBulkTenderReady,
		isBulkTenderEnabled,
	);
	const showCarrierPreference = shouldShowCarrierPreference(
		isBulkTenderAvailable,
		files.length,
		templateType,
	);
	const isSubmissionSuccess = submissionResult?.status === "success";

	const handleSubmissionClose = () => {
		if (isSubmissionSuccess) {
			confirmSubmissionSuccess();
			return;
		}
		dismissSubmissionResult();
	};

	return (
		<>
			<TraxModal
				data-testid={"bulk-upload-modal"}
				onClose={toggleBulkUploadModal}
				title={"Bulk Upload"}
				width={456}
			>
				{isUploading ? <LoadingSpinner /> : null}
				<div className={"modal-body"}>
					<div className={"bulk-upload-section"}>
						<div className={"bulk-upload-field-label"}>
							Select an Excel file to upload
						</div>
						<Upload
							autoUpload={false}
							files={files}
							onAdd={handleAdd}
							onRemove={handleRemove}
							restrictions={{
								allowedExtensions: [".xlsx", ".xls"],
								maxFileSize: 5_000_000,
							}}
							selectMessageUI={SelectFilesMessage}
							showActionButtons={false}
							withCredentials={false}
						/>
						<Hint>Only Excel files are allowed</Hint>
					</div>

					{showCarrierPreference ? (
						<fieldset className={"bulk-upload-carrier-preference"}>
							<legend>
								What criteria should we apply for the selected carrier?
								<span aria-hidden={true} className={"required"}>
									*
								</span>
							</legend>
							<RadioGroup
								aria-label={"Carrier selection criteria"}
								data={CARRIER_PREFERENCE_OPTIONS}
								item={CarrierPreferenceItem}
								layout={"vertical"}
								onChange={(event: { value: unknown }) => {
									const value = event.value;
									if (
										value === "lowest_cost" ||
										value === "lowest_cost_fastest" ||
										value === "lowest_cost_preferred" ||
										value === "manual"
									) {
										handleCarrierPreferenceChange(value as CarrierPreference);
									}
								}}
								value={carrierPreference}
							/>
						</fieldset>
					) : null}

					{errors.length > 0 && (
						<MessageBox status={"error"} title={"Upload Errors"}>
							<div>
								<ul>
									{errors.map((error) => (
										<li key={error}>{error}</li>
									))}
								</ul>
							</div>
						</MessageBox>
					)}
				</div>
				<DialogActionsBar>
					<div className={"trax-modal-confirmation-actions"}>
						<div className={"bulk-upload-template-actions"}>
							{canDownloadQuoteTemplate ? (
								<Button
									data-testid={"download-quote-template-button"}
									onClick={() => handleDownloadTemplate("quote")}
									svgIcon={download_01Line}
									variant={"tertiary"}
								>
									Quote Template
								</Button>
							) : null}
							{isBulkTenderAvailable ? (
								<Button
									data-testid={"download-tender-template-button"}
									onClick={() => handleDownloadTemplate("tender")}
									svgIcon={download_01Line}
									variant={"tertiary"}
								>
									Tender Template
								</Button>
							) : null}
						</div>
						<Button
							data-testid={"upload-file-button"}
							disabled={isUploadDisabled({
								isUploading,
								fileCount: files.length,
								templateType,
								carrierPreference,
								isLookupDataReady,
								isBulkTenderAvailable,
							})}
							onClick={() => {
								void uploadFile();
							}}
							svgIcon={upload_01Line}
							variant={"primary"}
						>
							{isUploading ? "Uploading..." : "Upload"}
						</Button>
					</div>
				</DialogActionsBar>
			</TraxModal>
			{submissionResult ? (
				<TraxModal
					data-testid={"bulk-upload-result-modal"}
					onClose={handleSubmissionClose}
					title={
						isSubmissionSuccess ? "Upload Complete" : submissionResult.title
					}
					type={isSubmissionSuccess ? "success" : "error"}
					width={440}
				>
					<div className={"bulk-upload-result-message"}>
						<p>{submissionResult.message}</p>
					</div>
					<DialogActionsBar>
						<div className={"trax-modal-confirmation-actions"}>
							<Button
								data-testid={"bulk-upload-result-button"}
								onClick={handleSubmissionClose}
								variant={"primary"}
							>
								{isSubmissionSuccess ? "Done" : "Close"}
							</Button>
						</div>
					</DialogActionsBar>
				</TraxModal>
			) : null}
		</>
	);
};
